import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dayjs } from 'dayjs';
import dayjs from '../../utils/dayJsCustomParseFormat';

/**
 * The day the orders list is showing lives in the query string rather than in state alone, so a
 * reload keeps the day, a link can be shared, and the dashboard's preorder card can point straight
 * at one day's orders.
 *
 * Both this format and the API's own `startOf('day')` are *local*, so a link shared across time
 * zones resolves to a different instant under the same label. That is fine for a Finland-only
 * backoffice, and the alternative — an instant in the URL — would show a different day than the one
 * written in the link.
 */
const DATE_PARAM = 'date';
const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * How far from today a link is allowed to point. A hand-edited or stale far-out date is parseable
 * but useless: it would strand the strip years away from today's orders with only the calendar to
 * get back.
 */
const MAX_DAYS_AWAY = 365;

/**
 * Strict, so only a real `YYYY-MM-DD` is honoured: without both the custom-parse plugin (which is
 * what this dayjs import carries) and the strict flag, `dayjs` falls back to loose `Date` parsing
 * and turns things like `2026-13-01` into some other day rather than rejecting them. Anything else
 * — truncated, hand-edited, absurdly far off — falls back to today instead of showing an invalid
 * day.
 */
const parseDateParam = (param: string | null): Dayjs => {
  const today = dayjs().startOf('day');
  if (!param) return today;

  const parsed = dayjs(param, DATE_FORMAT, true);
  if (!parsed.isValid()) return today;

  return Math.abs(parsed.startOf('day').diff(today, 'day')) > MAX_DAYS_AWAY ? today : parsed.startOf('day');
};

type OrdersDateParam = {
  /** The day to show; today when the URL says nothing usable. */
  date: Dayjs;
  setDate: (date: Dayjs) => void;
};

export const useOrdersDateParam = (): OrdersDateParam => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get(DATE_PARAM);

  // Memoised on the raw string, not the parsed object. Every location change re-renders this
  // subtree — opening an order panel, a filter change — and a fresh `Dayjs` each time would restart
  // the date strip's centring effect, gliding the row sideways on changes that are nothing to do
  // with the day. The string is a primitive, so the identity only moves when the day does.
  const date = useMemo(() => parseDateParam(rawDate), [rawDate]);

  const setDate = useCallback(
    (next: Dayjs) => {
      const formatted = next.format(DATE_FORMAT);
      // Tapping the day already selected would otherwise write the same value back, and a write is
      // a location change: a re-render of the whole view for nothing.
      if (formatted === rawDate) return;

      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(DATE_PARAM, formatted);

          return params;
        },
        // Replace, so walking along the strip does not bury the page the user came from under one
        // history entry per day they looked at. Every other param — `?order=` above all — is
        // carried over rather than dropped.
        { replace: true }
      );
    },
    [rawDate, setSearchParams]
  );

  return { date, setDate };
};
